import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, Subject, switchMap, catchError, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocRecord, DocTable, DocTarget } from './documents.models';
import { DocumentsService } from './documents.service';
import { viewRefresher } from './view-refresh';

/** Choose a record to link documents to: a table (the role can write) then one of its records (search) */
@Component({
  selector: 'iu-document-record-picker',
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (value(); as v) {
      <div class="rp-chosen">
        <i class="fa-solid fa-link" aria-hidden="true"></i>
        <span class="rp-chosen-text"><strong>{{ v.recordLabel || '#' + v.recordId }}</strong> <span class="rp-muted">{{ v.tableLabel }}</span></span>
        <button type="button" class="rp-link" (click)="clear()">{{ 'documents.picker.change' | translate }}</button>
      </div>
    } @else {
      <div class="rp-row">
        <label class="rp-field">
          <span>{{ 'documents.picker.table' | translate }}</span>
          @if (table(); as t) {
            <span class="rp-chip">{{ t.label }}
              <button type="button" class="rp-x" (click)="table.set(null); records.set([])" [attr.aria-label]="'documents.filters.remove' | translate"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
            </span>
          } @else {
            <input class="p-inputtext rp-input" [placeholder]="'documents.picker.tableHelp' | translate" (input)="tableQ$.next($any($event.target).value)" (focus)="tableQ$.next($any($event.target).value)" />
          }
        </label>
        @if (table()) {
          <label class="rp-field">
            <span>{{ 'documents.picker.record' | translate }}</span>
            <input class="p-inputtext rp-input" [placeholder]="'documents.picker.recordHelp' | translate" (input)="recordQ$.next($any($event.target).value)" (focus)="recordQ$.next($any($event.target).value)" />
          </label>
        }
      </div>
      @if (!table() && tables().length) {
        <ul class="rp-list" role="listbox">
          @for (t of tables(); track t.id) {
            <li role="option" [attr.aria-selected]="false"><button type="button" (click)="chooseTable(t)">{{ t.label }} <span class="rp-muted">{{ t.tableName }}</span></button></li>
          }
        </ul>
      }
      @if (table() && records().length) {
        <ul class="rp-list" role="listbox">
          @for (r of records(); track r.id) {
            <li role="option" [attr.aria-selected]="false"><button type="button" (click)="chooseRecord(r)">{{ r.label || '#' + r.id }} <span class="rp-muted">#{{ r.id }}</span></button></li>
          }
        </ul>
      }
      @if (searching()) {
        <p class="rp-muted"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i></p>
      } @else if (table() && searched() && !records().length) {
        <p class="rp-muted">{{ 'documents.picker.none' | translate }}</p>
      }
    }
  `,
  styles: `
    :host { display: block; }
    .rp-row { display: flex; flex-wrap: wrap; gap: 8px; }
    .rp-field { display: flex; flex-direction: column; gap: 3px; flex: 1 1 180px; font-size: 11px; color: var(--docs-ft-color-muted); }
    .rp-input { height: 28px; padding: 0 8px; font: inherit; font-size: var(--font-size); color: var(--global-text-color-1); border: 1px solid var(--input-bd-color); border-radius: var(--docs-br); background: var(--input-bg-color); }
    .rp-chip { display: inline-flex; align-items: center; gap: 4px; height: 28px; padding: 0 4px 0 10px; border-radius: 999px; background: var(--docs-bg-color-selected); color: var(--global-text-color-1); font-size: var(--font-size); }
    .rp-x { border: none; background: transparent; cursor: pointer; color: inherit; }
    .rp-list { margin: 6px 0 0; padding: 0; list-style: none; max-height: 180px; overflow: auto; border: 1px solid var(--docs-bd-color); border-radius: var(--docs-br); }
    .rp-list button { display: block; width: 100%; padding: 5px 8px; border: none; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; }
    .rp-list button:hover, .rp-list button:focus-visible { background: var(--docs-bg-color-hover); }
    .rp-muted { color: var(--docs-ft-color-muted); font-size: 11px; }
    .rp-chosen { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-radius: var(--docs-br); background: var(--docs-bg-color-hover); }
    .rp-chosen-text { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .rp-link { border: none; background: transparent; color: var(--docs-ft-color-link); cursor: pointer; font: inherit; }
  `,
})
export class DocumentRecordPickerComponent {
  readonly value = input<DocTarget | null>(null);
  readonly valueChange = output<DocTarget | null>();

  readonly #docs = inject(DocumentsService);
  readonly #refresh = viewRefresher();

  readonly table = signal<DocTable | null>(null);
  readonly tables = signal<DocTable[]>([]);
  readonly records = signal<DocRecord[]>([]);
  readonly searching = signal(false);
  readonly searched = signal(false);
  readonly tableQ$ = new Subject<string>();
  readonly recordQ$ = new Subject<string>();

  constructor() {
    this.tableQ$
      .pipe(
        debounceTime(250),
        switchMap((q) => {
          this.searching.set(true);
          return this.#docs.tables(q.trim()).pipe(catchError(() => of([] as DocTable[])));
        }),
        takeUntilDestroyed()
      )
      .subscribe((t) => {
        this.searching.set(false);
        this.tables.set(t);
        this.#refresh();
      });
    this.recordQ$
      .pipe(
        debounceTime(250),
        switchMap((q) => {
          const t = this.table();
          if (!t) return of([] as DocRecord[]);
          this.searching.set(true);
          return this.#docs.records(t.id, q.trim()).pipe(catchError(() => of([] as DocRecord[])));
        }),
        takeUntilDestroyed()
      )
      .subscribe((r) => {
        this.searching.set(false);
        this.searched.set(true);
        this.records.set(r);
        this.#refresh();
      });
  }

  chooseTable(t: DocTable): void {
    this.table.set(t);
    this.tables.set([]);
    this.searched.set(false);
    this.recordQ$.next('');
  }

  chooseRecord(r: DocRecord): void {
    const t = this.table();
    if (!t) return;
    this.valueChange.emit({ tableId: t.id, tableLabel: t.label, recordId: r.id, recordLabel: r.label });
  }

  clear(): void {
    this.table.set(null);
    this.records.set([]);
    this.valueChange.emit(null);
  }
}
